import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  useTheme,
  alpha,
  Container
} from '@mui/material';
import {
  People as PeopleIcon,
  Store as StoreIcon,
  Restaurant as RestaurantIcon,
  LocalShipping as DeliveryIcon,
  ShoppingCart as OrderIcon,
  CheckCircle as DeliveredIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useNavigate } from 'react-router-dom';
// import { useGetAllStoreOwnersQuery } from '../../Query/AddStoreQuery';
import CustomersGraph from './CustomersGraph';
import MerchantGraph from './MerchantGraph';
import RestaurantGraph from './RestaurantGraph';
import DeliveryBoyGraph from './DeliveryBoyGraph';
import OrderStatusGraph from './OrderStatusGraph';
import RevenueGraph from './RevenueGraph';
// import { useManageCustomer } from '../../Stateless/ManageCustomer/useManageCustomer';
import { useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
// import { useStoreOwnerparams } from '../StoreOwner/useStoreOwnerparams';
// import { useGetAllCustomer } from '../../Query/CustomerQuery';
import { useGetAllDashboard } from '../../Query/DashboardQuery';
import { COLORS } from '../../../theme/themeConstants';

// Metrics will be built from API response

// Orders chart data will be built from API response

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Local filter state with defaults: past 1 year to today
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(() => dayjs());
  const [appliedFromDate, setAppliedFromDate] = useState<Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [appliedToDate, setAppliedToDate] = useState<Dayjs | null>(() => dayjs());

  const fromDate = useMemo(() => appliedFromDate?.format('YYYY-MM-DD'), [appliedFromDate]);
  const toDate = useMemo(() => appliedToDate?.format('YYYY-MM-DD'), [appliedToDate]);

  // Previous table data hooks removed; now using dashboard API response directly

  const { data: dashboardData} = useGetAllDashboard({
    dashboardType: 'admin',
    fromDate,
    toDate,
  }as any);

  const metricsData = useMemo(() => {
    const count: any = (dashboardData as any)?.metricsCards ?? {};
    return [
      { title: 'Customers', value: Number(count.customersCount) || 0, icon: PeopleIcon, color: '#1976d2' },
      { title: 'Merchants', value: Number(count.merchantCount || count.merchantsCount) || 0, icon: StoreIcon, color: '#388e3c' },
      { title: 'Store', value: Number(count.storesCount) || 0, icon: RestaurantIcon, color: '#f57c00' },
      { title: 'Store Items', value: Number(count.storesItemsCount) || 0, icon: RestaurantIcon, color: '#7b1fa2' },
      { title: 'Delivery Members', value: Number(count.deliveryMembersCount) || 0, icon: DeliveryIcon, color: '#d32f2f' },
      { title: 'Orders', value: Number(count.ordersCount) || 0, icon: OrderIcon, color: '#1976d2' },
      { title: 'Delivered Orders', value: Number(count.deliveredOrdersCount) || 0, icon: DeliveredIcon, color: '#388e3c' },
    ];
  }, [dashboardData]);

  // const { data: merchantData, isLoading: merchantLoading } = useGetAllStoreOwnersQuery(memoizedParam);

  // Process customer data
  const customersData = (dashboardData as any)?.recentCustomersList?.slice(0, 5)?.map((customer: any, index: number) => ({
    id: index + 1,
    name: customer.customerName || customer.name || 'N/A',
    email: customer.email || customer.customerEmail || 'N/A',
    phone: customer.customerPhone || customer.mobileNumber || 'N/A',
    status: customer.status === 'unblocked' ? 'Active' : (customer.status || 'Inactive')
  })) || [];

  // Process merchant data
  const merchantsData = (dashboardData as any)?.recentMerchantsList?.slice(0, 5)?.map((merchant: any, index: number) => ({
    id: index + 1,
    name: merchant.merchantName || `${merchant.firstName || ''} ${merchant.lastName || ''}`.trim() || 'N/A',
    email: merchant.email || 'N/A',
    phone: merchant.phone || merchant.mobileNo || 'N/A',
    status: merchant.status === 'unblocked' ? 'Active' : (merchant.isActive ? 'Active' : 'Inactive')
  })) || [];

  const MetricCard = ({ title, value, icon: Icon, color }: any) => (
    <Card
      sx={{
        height: 160,
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${alpha(color, 0.3)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 28, color: color }} />
          </Box>
          {/* Removed trend chip */}
        </Box>
        <Box>
          <Typography variant="h3" component="div" sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: '2rem',
            lineHeight: 1.2
          }}>
            {value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            opacity: 0.8,
            mb: 1
          }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{
            fontSize: '0.75rem',
            opacity: 0.6,
            lineHeight: 1.2
          }}>
            {title === 'Customers' && 'Total registered users on the platform'}
            {title === 'Merchants' && 'Active business partners and vendors'}
            {title === 'Store' && 'Stores available on the platform'}
            {title === 'Store Items' && 'Available items across stores'}
            {title === 'Delivery Members' && 'Active delivery personnel'}
            {title === 'Orders' && 'Total orders placed by customers'}
            {title === 'Delivered Orders' && 'Successfully completed deliveries'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const DataTable = ({ title, data, columns, showToggle = true, loading = false, onViewAll }: any) => (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        background: '#ffffff',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        p: 3,
        borderBottom: '1px solid #e8eaed',
        background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: 'text.primary',
          fontSize: '1.125rem'
        }}>
          {title}
        </Typography>
      </Box>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafbfc' }}>
              {columns.map((column: any) => (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    borderBottom: '2px solid #e8eaed',
                    fontSize: '0.875rem',
                    py: 2
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row: any,) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafbfc'
                    }
                  }}
                >
                  {columns.map((column: any) => (
                    <TableCell key={column.id} sx={{ py: 2, fontSize: '0.875rem' }}>
                      {column.id === 'status' ? (
                        <Chip
                          label={row[column.id]}
                          size="small"
                          sx={{
                            backgroundColor: row[column.id] === 'Active' ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24
                          }}
                        />
                      ) : (
                        row[column.id]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* {showToggle && (
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid #e8eaed',
          background: '#fafbfc',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
         }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch size="small" />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Auto-refresh
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            onClick={onViewAll}
            sx={{ 
              background: 'linear-gradient(135deg, #ff4757 0%, #ff3742 100%)',
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': { 
                background: 'linear-gradient(135deg, #ff3742 0%, #ff2e3a 100%)',
                boxShadow: '0 4px 12px rgba(255, 71, 87, 0.3)'
              }
            }}
          >
            View All {title}
          </Button>
        </Box>
      )} */}
      {showToggle && (
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid #e8eaed',
            background: '#fafbfc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={onViewAll}
            sx={{
              background: COLORS.primary.main,
              color: COLORS.secondary.main,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                background: COLORS.primary.dark,
                boxShadow: '0 4px 12px rgba(255, 71, 87, 0.3)',
              },
            }}
          >
            View All {title}
          </Button>
        </Box>
      )}
    </Paper>
  );

  const handleViewAllCustomers = () => {
    navigate('/manageCustomer');
  };

  const handleViewAllMerchants = () => {
    navigate('/store-owner');
  };

  // Validation function for date selection
  const validateDateSelection = () => {
    if (!selectedFromDate || !selectedToDate) {
      return { isValid: false, message: 'Please select both from and to dates' };
    }
    
    if (selectedFromDate.isAfter(selectedToDate)) {
      return { isValid: false, message: 'End date must be greater than or equal to start date' };
    }
    
    if (selectedFromDate.isAfter(dayjs())) {
      return { isValid: false, message: 'Start date cannot be in the future' };
    }
    
    if (selectedToDate.isAfter(dayjs())) {
      return { isValid: false, message: 'End date cannot be in the future' };
    }
    
    return { isValid: true, message: '' };
  };

  // Check if current date selection is valid
  const isDateSelectionValid = () => {
    return validateDateSelection().isValid;
  };

  // Handle Apply button click
  const handleApplyFilters = () => {
    const validation = validateDateSelection();
    
    if (!validation.isValid) {
      // You can replace this with a proper notification system
      alert(validation.message);
      return;
    }
    
    // Apply the selected dates
    setAppliedFromDate(selectedFromDate);
    setAppliedToDate(selectedToDate);
    // Note: No manual refetch here. Changing the query params
    // (fromDate/toDate derived from applied dates) will trigger
    // the query to refetch once via React Query automatically.
  };

  // Handle Reset button click
  const handleResetFilters = () => {
    const defaultFromDate = dayjs().subtract(1, 'year');
    const defaultToDate = dayjs();
    
    setSelectedFromDate(defaultFromDate);
    setSelectedToDate(defaultToDate);
    setAppliedFromDate(defaultFromDate);
    setAppliedToDate(defaultToDate);
    // Note: No manual refetch here. Updating applied dates will
    // update query params and trigger a single refetch automatically.
  };

  // Check if dates have been changed from defaults
  const hasDateChanged = () => {
    const defaultFromDate = dayjs().subtract(1, 'year');
    const defaultToDate = dayjs();
    
    return !selectedFromDate?.isSame(defaultFromDate, 'day') || 
           !selectedToDate?.isSame(defaultToDate, 'day');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)',
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Metrics Cards */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: '1.75rem'
          }}>
            Dashboard Overview
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 3,
              mb: 3,
              flexWrap: 'wrap',
              p: 2,
              background: '#ffffff',
              border: '1px solid #e8eaed',
              borderRadius: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>From date</Typography>
                <DatePicker
                  value={selectedFromDate}
                  onChange={(newValue) => setSelectedFromDate(newValue)}
                  format="MM/DD/YYYY"
                  maxDate={selectedToDate || dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 230 },
                      error: !!(selectedFromDate && selectedToDate && selectedFromDate.isAfter(selectedToDate)),
                      helperText: selectedFromDate && selectedToDate && selectedFromDate.isAfter(selectedToDate) 
                        ? 'Start date cannot be after end date' 
                        : ''
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>To date</Typography>
                <DatePicker
                  value={selectedToDate}
                  onChange={(newValue) => setSelectedToDate(newValue)}
                  format="MM/DD/YYYY"
                  minDate={selectedFromDate || undefined}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 230 },
                      error: !!(selectedFromDate && selectedToDate && selectedToDate.isBefore(selectedFromDate)),
                      helperText: selectedFromDate && selectedToDate && selectedToDate.isBefore(selectedFromDate) 
                        ? 'End date must be greater than or equal to start date' 
                        : ''
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            <Button
              variant="contained"
              size="medium"
              disabled={!isDateSelectionValid()}
              sx={{
                ml: 2,
                mb:1,
                textTransform: 'none',
                px: 3,
                backgroundColor: isDateSelectionValid() ? COLORS.primary.main : '#ccc',
                '&:hover': { 
                  backgroundColor: isDateSelectionValid() ? alpha(COLORS.primary.main, 0.8) : '#ccc'
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666'
                }
              }}
              onClick={handleApplyFilters}
            >
              Apply
            </Button>
            {hasDateChanged() && (
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  ml: 1,
                  mb:1,
                  textTransform: 'none',
                  px: 3,
                  borderColor: '#666',
                  color: '#666',
                  '&:hover': { 
                    borderColor: '#333',
                    color: '#333',
                    backgroundColor: alpha('#666', 0.1)
                  },
                }}
                onClick={handleResetFilters}
              >
                Reset
              </Button>
            )}
          </Box>
          
          {/* Validation Message */}
          {!isDateSelectionValid() && (
            <Box sx={{ 
              mt: 1, 
              p: 2, 
              backgroundColor: '#ffebee', 
              border: '1px solid #f44336', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                ⚠️ {validateDateSelection().message}
              </Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{
            color: 'text.secondary',
            mb: 3,
            fontSize: '1rem',
            opacity: 0.8
          }}>
            Key performance indicators and metrics for your platform
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 3
          }}>
            {metricsData.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </Box>
        </Box>

        {/* Orders Chart */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: '#ffffff',
            border: '1px solid #e8eaed',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            Order Trends
          </Typography>
          <Box sx={{ height: 350 }}>
            {useMemo(() => {
              const monthsOrder = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const apiSeries: any[] = ((dashboardData as any)?.orderTrendsLineChart || []) as any[];
              const byMonth = new Map<string, number>();
              apiSeries.forEach((item: any) => {
                if (item?.month && typeof item?.totalOrders !== 'undefined') {
                  byMonth.set(item.month.slice(0,3), Number(item.totalOrders) || 0);
                }
              });
              const chartData = monthsOrder.map((m) => ({ month: m, orders: byMonth.get(m) || 0 }));
              const hasData = chartData.some(item => item.orders > 0);
              
              if (!hasData) {
                return (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No order trends data for the selected date range
                    </Typography>
                  </Box>
                );
              }
              
              return (
                <LineChart
                  dataset={chartData}
                  xAxis={[{
                    scaleType: 'band',
                    dataKey: 'month',
                    tickLabelStyle: {
                      fontSize: 12,
                      fontWeight: 500,
                    }
                  }]}
                  yAxis={[{
                    min: 0,
                    tickLabelStyle: {
                      fontSize: 12,
                      fontWeight: 500,
                    }
                  }]}
                  series={[
                    {
                      dataKey: 'orders',
                      label: 'Orders',
                      color: '#1976d2',
                      curve: 'linear',
                      area: true,
                    },
                  ]}
                  height={300}
                  margin={{ top: 20, bottom: 30, left: 40, right: 20 }}
                  sx={{
                    '.MuiLineElement-root': {
                      strokeWidth: 3,
                      stroke: '#1976d2',
                    },
                    '.MuiMarkElement-root': {
                      stroke: '#1976d2',
                      strokeWidth: 2,
                      fill: '#ffffff',
                    },
                    '.MuiAreaElement-root': {
                      fill: 'url(#areaGradient)',
                    },
                  }}
                >
                  <defs>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#1976d2" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#1976d2" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                </LineChart>
              );
            }, [dashboardData])}
          </Box>
        </Paper>

        {/* Charts Row - Side by Side */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            Analytics Overview
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Customers Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const palette = ['#e74c3c', '#3498db', '#2ecc71', '#9b59b6'];
                    const byTypeColor: Record<string, string> = {
                      admin: '#e74c3c',
                      google: '#3498db',
                      normal: '#2ecc71',
                    };
                    const src = (dashboardData as any)?.customerRegistrationTypePieChart || [];
                    const chartData = src.map((d: any, idx: number) => ({
                      label: d.type,
                      value: Number(d.percentage) || 0,
                      color: byTypeColor[(d.type || '').toLowerCase()] || palette[idx % palette.length],
                    }));
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No customer data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <CustomersGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Merchant Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const chartData = (dashboardData as any)?.merchantStatusPieChart?.map((d: any, idx: number) => ({
                      label: d.status,
                      value: Number(d.percentage) || 0,
                      color: ['#2ecc71', '#e74c3c', '#b0bec5'][idx % 3],
                    })) || [];
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No merchant data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <MerchantGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Store Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const chartData = (dashboardData as any)?.storeStatusPieChart?.map((d: any, idx: number) => ({
                      label: d.status,
                      value: Number(d.percentage) || 0,
                      color: ['#2ecc71', '#e74c3c', '#f39c12'][idx % 3],
                    })) || [];
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No store data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <RestaurantGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Delivery Boy Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const chartData = (dashboardData as any)?.deliveryBoyStatusPieChart?.map((d: any, idx: number) => ({
                      label: d.status,
                      value: Number(d.percentage) || 0,
                      color: ['#3498db', '#eb9c52', '#95a5a6'][idx % 3],
                    })) || [];
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No delivery data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <DeliveryBoyGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* New Pie Charts Row */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            Order & Revenue Analytics
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Order Status Distribution
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const chartData = (dashboardData as any)?.orderStatusPieChart?.map((d: any, idx: number) => ({
                      label: d.status,
                      value: Number(d.percentage) || 0,
                      color: ['#2ecc71', '#3498db', '#f39c12', '#e74c3c'][idx % 4],
                    })) || [];
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No order status data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <OrderStatusGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Revenue by Category
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  {(() => {
                    const chartData = (dashboardData as any)?.revenueByStoreTypePieChart?.map((d: any, idx: number) => ({
                      label: String(d.storeType ?? ''),
                      value: Number(d.percentage) || 0,
                      color: ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'][idx % 8],
                    })) || [];
                    const hasData = chartData.some((item: any) => item.value > 0);
                    
                    if (!hasData) {
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          <Typography variant="h6" color="text.secondary">
                            No data available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No revenue data for selected period
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return <RevenueGraph data={chartData} />;
                  })()}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Tables Row - Side by Side */}
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            Recent Activity
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            <Box sx={{ width: '100%' }}>
              <DataTable
                title="Customers"
                data={customersData}
                loading={false}
                columns={[
                  { id: 'id', label: 'S.No' },
                  { id: 'name', label: 'Customer Name' },
                  { id: 'email', label: 'Email' },
                  { id: 'phone', label: 'Customer Phone' },
                  { id: 'status', label: 'Status' },
                ]}
                onViewAll={handleViewAllCustomers}
              />
            </Box>

            <Box sx={{ width: '100%' }}>
              <DataTable
                title="Merchant"
                data={merchantsData}
                loading={false}
                columns={[
                  { id: 'id', label: 'S.No' },
                  { id: 'name', label: 'Merchant Name' },
                  { id: 'email', label: 'Email' },
                  { id: 'phone', label: 'Phone' },
                  { id: 'status', label: 'Status' },
                ]}
                onViewAll={handleViewAllMerchants}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
